// ============================================================================
// BOT CONTROLLER INTERFACE
// Abstraction layer for bot AI systems (Rule-based, SLM, etc.)
// ============================================================================

import { PlayerInput, ObjectiveState, Vector2 } from '@/shared/types';
import { Player } from '@/game/entities/Player';
import { GameMap } from '@/game/map/GameMap';
import { CollisionWorld } from '@/engine/CollisionSystem';

/**
 * Bot AI Mode - determines which AI system controls bots
 */
export enum BotAIMode {
  RuleBased = 'rule_based',   // Traditional state machine AI (current)
  SLM = 'slm',                // Small Language Model AI (future)
}

/**
 * Game context provided to bot controllers each frame
 */
export interface BotGameContext {
  dt: number;                      // Delta time in ms
  time: number;                    // Current game time
  allPlayers: Player[];            // All players in the game
  collisionWorld: CollisionWorld;  // For raycasting and collision checks
  map: GameMap;                    // Current game map
  objectives?: ObjectiveState;     // Spike/objective state
}

/**
 * Simple tactical intent enum - for quick decision making
 */
export enum TacticalIntent {
  Aggressive = 'aggressive',    // Push forward, engage enemies
  Defensive = 'defensive',      // Hold position, wait for enemies
  Flanking = 'flanking',        // Move around to side/behind enemies
  Supporting = 'supporting',    // Follow teammates, provide backup
  Objective = 'objective',      // Focus on spike plant/defuse
  Retreating = 'retreating',    // Fall back to safety
  Patrolling = 'patrolling',    // Move around, look for enemies
}

/**
 * Detailed tactical decision from SLM (used by SLMBotController)
 */
export interface TacticalDecision {
  intent: TacticalIntent;          // High-level intent
  target?: string;                 // Target location or player
  priority: number;                // 0-1, how important this action is
  reasoning?: string;              // Optional: why this decision was made (for debugging)
}

/**
 * Bot state information exposed for SLM decision making
 */
export interface BotStateInfo {
  // Bot info
  botId: string;
  position: Vector2;
  health: number;
  shield: number;
  team: string;
  isAlive: boolean;
  
  // Weapon state
  ammo: number;
  maxAmmo: number;
  isReloading: boolean;
  weaponName: string;
  
  // Abilities
  ability1Ready: boolean;
  ability2Ready: boolean;
  ultimateReady: boolean;
  ultimateCharge: number;
  
  // Awareness
  visibleEnemies: Array<{
    id: string;
    position: Vector2;
    health: number;
    distance: number;
  }>;
  nearbyTeammates: Array<{
    id: string;
    position: Vector2;
    health: number;
    distance: number;
  }>;
  
  // Objectives
  hasSpikeCarrier: boolean;
  spikePlanted: boolean;
  spikePosition?: Vector2;
  inSpikeSite: boolean;
  
  // Map context (simplified for SLM)
  nearestCoverDirection?: Vector2;
  distanceToNearestWall: number;
  currentZone: string;  // 'spawn', 'mid', 'site_a', 'site_b', etc.
}

/**
 * Interface for bot controllers
 * Both rule-based and SLM-based controllers must implement this
 */
export interface IBotController {
  /**
   * Unique identifier for this controller type
   */
  readonly type: BotAIMode;
  
  /**
   * Update the bot and return input for this frame
   * @param context Game context for decision making
   * @returns PlayerInput to apply to the bot
   */
  update(context: BotGameContext): PlayerInput;
  
  /**
   * Get the current bot state info (for SLM or debugging)
   */
  getStateInfo(): BotStateInfo;
  
  /**
   * Get the player this controller is managing
   */
  getPlayer(): Player;
  
  /**
   * Set a high-level tactical intent (used by SLM to override behavior)
   * Rule-based AI can ignore this or use it as a hint
   */
  setTacticalIntent?(intent: TacticalIntent): void;
  
  /**
   * Get current tactical intent (for debugging/visualization)
   */
  getTacticalIntent?(): TacticalIntent | null;
  
  /**
   * Called when the round starts - reset state
   */
  onRoundStart?(): void;
  
  /**
   * Called when the round ends - cleanup
   */
  onRoundEnd?(): void;
  
  /**
   * Cleanup resources when bot is removed
   */
  dispose?(): void;
}

/**
 * Factory function type for creating bot controllers
 */
export type BotControllerFactory = (
  player: Player,
  difficulty: 'easy' | 'medium' | 'hard'
) => IBotController;
